// src/components/templates/TemplateCard.jsx
//
// One template in the gallery grid, as the prototype draws it: the book cover on
// a soft ground, two tags, the name, a three-line description, the word range,
// and two actions — use it to start a draft, or preview it.
//
// "Preview" (and clicking the cover) replaces the grid with the template's
// detail page; the gallery owns that switch. "Use Template" opens the app's own
// Create dialog with this template chosen.
import React from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import TemplateCover from './TemplateCover';
import { SP, FONT } from './templateTokens';
import { tokens } from '../../theme';

/** "80,000–100,000" reads as a word count only with the unit after it. */
export const wordsLabel = (words) => (/\d$/.test(words || '') ? `${words} words` : words || '');

export const primaryButtonSx = {
  fontFamily: FONT.sans,
  background: SP.purple,
  color: SP.white,
  border: 'none',
  borderRadius: '8px',
  fontWeight: 600,
  cursor: 'pointer',
  transition: `background ${tokens.motion.fast}ms ${tokens.motion.ease}`,
  '&:hover': { background: SP.purpleDeep },
  '&:focus-visible': { outline: `2px solid ${SP.purpleMid}`, outlineOffset: '2px' },
};

export const linkButtonSx = {
  fontFamily: FONT.sans,
  background: 'none',
  border: 'none',
  color: SP.purple,
  fontWeight: 600,
  cursor: 'pointer',
  p: 0,
  '&:hover': { textDecoration: 'underline' },
  '&:focus-visible': { outline: `2px solid ${SP.purpleMid}`, outlineOffset: '2px' },
};

export default function TemplateCard({ template, onUse, onPreview }) {
  const words = wordsLabel(template.words);

  return (
    <Box
      component="article"
      data-testid="template-card"
      data-template-id={template.id}
      sx={{
        background: SP.white,
        border: `1px solid ${SP.lineSoft}`,
        borderRadius: '14px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: `transform 250ms ${tokens.motion.ease}, box-shadow 250ms ${tokens.motion.ease}, border-color 250ms`,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 14px 28px ${alpha(SP.purpleDeep, 0.1)}`,
          borderColor: SP.purpleSoft,
        },
        '&:hover .tpl-book': { transform: 'scale(1.04) rotate(-.5deg)' },
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onPreview}
        aria-label={`Preview ${template.name}`}
        sx={{
          height: 212,
          border: 'none',
          p: 0,
          cursor: 'pointer',
          background: `radial-gradient(circle at 50% 35%, ${SP.white}, ${SP.lavender})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:focus-visible': { outline: `2px solid ${SP.purpleMid}`, outlineOffset: '-2px' },
        }}
      >
        <TemplateCover
          className="tpl-book"
          id={template.id}
          name={template.name}
          sx={{ width: 120, height: 180, transition: `transform 300ms ${tokens.motion.ease}` }}
        />
      </Box>

      <Box sx={{ p: '16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {template.tags?.length > 0 && (
          <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mb: '8px' }}>
            {template.tags.slice(0, 2).map((tag) => (
              <Box
                key={tag}
                component="span"
                sx={{ fontSize: 11, color: SP.gray, background: SP.lavender, px: '8px', py: '2px', borderRadius: '5px', fontWeight: 500 }}
              >
                {tag}
              </Box>
            ))}
          </Box>
        )}

        <Box component="h3" sx={{ m: 0, mb: '6px', fontSize: 15.5, fontWeight: 700, letterSpacing: '-.01em', color: SP.black }}>
          {template.name}
        </Box>

        <Box
          component="p"
          sx={{
            m: 0,
            mb: '12px',
            fontSize: 12.5,
            color: SP.gray,
            lineHeight: 1.45,
            flex: 1,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {template.description}
        </Box>

        {words && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: 11.5, color: SP.gray, mb: '12px' }}>
            <EditOutlinedIcon sx={{ fontSize: 13 }} />
            {words}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: `1px solid ${SP.lavender}`,
            pt: '12px',
          }}
        >
          <Box
            component="button"
            type="button"
            data-testid="template-use"
            onClick={onUse}
            sx={{ ...primaryButtonSx, px: '14px', py: '8px', fontSize: 12.5 }}
          >
            Use Template
          </Box>
          <Box
            component="button"
            type="button"
            data-testid="template-preview"
            onClick={onPreview}
            sx={{ ...linkButtonSx, fontSize: 12.5 }}
          >
            Preview
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
